import { type Component, createEffect, createSignal, Show } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Chip from '~/components/Chip'
import Icon from '~/components/Icon'
import Select from '~/components/Select'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import {
  type AICategoryType,
  type AIConnectionModelType,
  type AIConnectionType,
  type AIProviderCapabilityType,
  type AIProviderConfigType,
  aiService,
  type ProviderCatalogEntryType
} from '~/services/ai.service'
import { resolveError } from '~/utils/error'

interface AIProviderCardPropsType {
  category: AICategoryType
  title: string
  subtitle: string
  icon: string
  catalog: ProviderCatalogEntryType[]
  connections: AIConnectionType[]
  /** Modelos guardados de todas las conexiones. */
  models: AIConnectionModelType[]
  capabilities: AIProviderCapabilityType[]
  config?: AIProviderConfigType
  onSaved: () => void | Promise<void>
}

/**
 * Configura UNA capacidad (chat, imagen, audio...). Ya no pide credenciales:
 * elige una CONEXIÓN de las que se registraron arriba, y el modelo. Sólo ofrece
 * las conexiones cuyo proveedor implementa esta categoría, así no puedes pedir
 * imágenes a un proveedor que no las hace.
 */
const AIProviderCard: Component<AIProviderCardPropsType> = props => {
  const toast = useToast()
  const alert = useAlert()

  const [connectionId, setConnectionId] = createSignal<number | null>(null)
  const [model, setModel] = createSignal('')
  const [loadingModels, setLoadingModels] = createSignal(false)
  const [saving, setSaving] = createSignal(false)
  /**
   * ¿El usuario ya tocó los controles?
   *
   * Antes esto era un `loaded` que se marcaba en el primer render con catálogo.
   * Pero `config` llega de otro recurso, casi siempre más tarde: el efecto
   * corría con todo vacío, se daba por cargado y la tarjeta se quedaba sin
   * mostrar nunca qué conexión tenía configurada. Ahora se resincroniza mientras
   * nadie haya elegido nada a mano.
   */
  const [touched, setTouched] = createSignal(false)

  /** Los proveedores que implementan esta categoría (lo declara el backend). */
  const providersForCategory = () => new Set(props.catalog.filter(p => p.categories.includes(props.category)).map(p => p.name))

  /** Comprueba si un proveedor tiene habilitada esta categoría para la empresa. */
  const isCapabilityEnabled = (providerName: string) => {
    const cap = props.capabilities.find(c => c.provider === providerName && c.category === props.category)
    // Sin fila = habilitado por defecto (retrocompatible)
    return !cap || cap.enabled === 1
  }

  /** Sólo las conexiones cuyo proveedor sirve para esta categoría Y está habilitado. */
  const usable = () => props.connections.filter(c => providersForCategory().has(c.provider) && isCapabilityEnabled(c.provider))
  const currentConn = () => usable().find(c => c.id === connectionId())
  const currentDef = () => props.catalog.find(p => p.name === currentConn()?.provider)

  const options = () => usable().map(c => ({ value: String(c.id), title: c.label, subtitle: providerLabel(c.provider) }))
  const providerLabel = (name: string) => props.catalog.find(p => p.name === name)?.label ?? name

  createEffect(() => {
    const config = props.config
    // Leer las tres fuentes deja el efecto suscrito a las tres: la que llegue
    // tarde vuelve a disparar la sincronización.
    const listo = props.catalog.length > 0 && props.connections.length > 0
    if (touched() || !listo) return

    setConnectionId(config?.connectionId ?? usable()[0]?.id ?? null)
    setModel(config?.model ?? '')
  })

  const changeConnection = (id: number | null) => {
    setTouched(true)
    setConnectionId(id)
    // El modelo era de otra cuenta, que puede ser de otro proveedor.
    setModel('')
  }

  /**
   * Los modelos guardados de la cuenta elegida.
   *
   * Antes esto era un campo de texto libre con un botón que consultaba al
   * proveedor y pintaba un segundo desplegable: dos controles para el mismo
   * dato, y lo consultado se perdía al cerrar. Ahora la lista se persiste al
   * probar la conexión y aquí sólo se elige.
   */
  const modelOptions = () => {
    const id = connectionId()
    if (!id) return []
    const guardados = props.models.filter(m => m.connectionId === id && m.available)
    const actual = model()
    // Un modelo configurado que el proveedor ya no lista sigue apareciendo: si se
    // escondiera, guardar la tarjeta lo perdería sin avisar.
    const retirado = actual && !guardados.some(m => m.model === actual) ? [{ value: actual, title: `${actual} (ya no disponible)` }] : []
    return [...retirado, ...guardados.map(m => ({ value: m.model, title: m.model }))]
  }

  /** Vuelve a preguntarle al proveedor qué modelos ofrece y los guarda. */
  const refreshModels = async () => {
    const id = connectionId()
    if (!id) return
    setLoadingModels(true)
    try {
      const resultado = await aiService.testConnection(id)
      toast.addToast({
        title: resultado.ok ? `${resultado.models} modelos actualizados` : 'No se pudo consultar el proveedor',
        description: resultado.error ?? undefined,
        type: resultado.ok ? 'success' : 'danger'
      })
      await props.onSaved()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setLoadingModels(false)
    }
  }

  const save = async () => {
    const id = connectionId()
    if (!id) {
      toast.addToast({ title: 'Elige una conexión', type: 'danger' })
      return
    }
    const chosenModel = model() || currentDef()?.defaultModels?.[props.category] || ''
    if (!chosenModel) {
      toast.addToast({ title: 'Indica un modelo', type: 'danger' })
      return
    }

    setSaving(true)
    try {
      await aiService.saveProvider(props.category, { connectionId: id, model: chosenModel })
      toast.addToast({ title: `${props.title}: guardado`, type: 'success' })
      setTouched(false)
      await props.onSaved()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    const ok = await alert.confirmDelete({
      title: `¿Quitar la cuenta de ${props.title.toLowerCase()}?`,
      message: 'La capacidad dejará de funcionar hasta que elijas otra.',
      confirmText: 'Sí, quitar'
    })
    if (!ok) return
    try {
      await aiService.deleteProvider(props.category)
      setModel('')
      setTouched(false)
      toast.addToast({ title: `${props.title}: proveedor quitado`, type: 'success' })
      await props.onSaved()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  return (
    <Card class="flex flex-col">
      <Card.Title>
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-2 min-w-0">
            <Icon icon={props.icon} size={20} class="shrink-0" />
            <div class="min-w-0">
              <h3 class="font-semibold text-foreground truncate">{props.title}</h3>
              <p class="text-xs text-foreground-subtle truncate">{props.subtitle}</p>
            </div>
          </div>
          <Show
            when={props.config}
            fallback={
              <Chip size="sm" variant="soft" color="default">
                Sin configurar
              </Chip>
            }
          >
            <Chip size="sm" variant="soft" color="success">
              Configurado
            </Chip>
          </Show>
        </div>
      </Card.Title>

      <Card.Body class="flex-1">
        {/* Sin proveedores para la categoría es distinto de sin conexiones: el
            primero es una limitación del catálogo; el segundo, algo que arreglas
            registrando una conexión arriba. */}
        <Show when={providersForCategory().size} fallback={<p class="text-sm text-foreground-subtle">Ninguno de tus proveedores genera {props.title.toLowerCase()}.</p>}>
          <Show
            when={usable().length}
            fallback={
              <p class="text-sm text-foreground-subtle">No tienes ninguna conexión habilitada para {props.title.toLowerCase()}. Añade una arriba o habilita la capacidad en la tabla de proveedores.</p>
            }
          >
            <div class="space-y-3">
              <Select label="Conexión" value={connectionId() != null ? String(connectionId()) : ''} options={options()} onChange={v => changeConnection(v ? Number(v) : null)} />

              <div>
                <div class="flex items-start gap-2">
                  <div class="flex-1">
                    <Select
                      label="Modelo"
                      value={model()}
                      options={modelOptions()}
                      onChange={v => {
                        setTouched(true)
                        setModel(String(v ?? ''))
                      }}
                      placeholder={currentDef()?.defaultModels?.[props.category] ?? 'elige un modelo'}
                    />
                  </div>
                  <Show when={currentDef()?.canListModels}>
                    <Button
                      variant="outlined"
                      color="default"
                      icon={loadingModels() ? 'svg-spinners:180-ring-with-bg' : 'material-symbols-light:refresh'}
                      iconSize={18}
                      class="py-3.5"
                      onClick={refreshModels}
                      disabled={loadingModels()}
                      aria-label="Actualizar los modelos de esta cuenta"
                      tooltip="Volver a preguntarle al proveedor qué modelos ofrece"
                    />
                  </Show>
                </div>
                <Show when={connectionId() && modelOptions().length === 0}>
                  <p class="text-xs text-foreground-subtle mt-1">Esta cuenta aún no tiene modelos guardados: pruébala con el botón de al lado.</p>
                </Show>
                <Show when={modelOptions().length > 0}>
                  <p class="text-xs text-foreground-subtle mt-1">Son todos los modelos de la cuenta, no sólo los de {props.title.toLowerCase()}.</p>
                </Show>
              </div>
            </div>
          </Show>
        </Show>
      </Card.Body>

      <Show when={usable().length}>
        <Card.Footer>
          <div class="flex items-center justify-between gap-2">
            <p class="text-xs text-foreground-subtle truncate">
              <Show when={props.config} fallback="Sin guardar, esta capacidad no funciona.">
                {providerLabel(props.config?.provider ?? '')} · {props.config?.model}
              </Show>
            </p>
            <div class="flex items-center gap-2 shrink-0">
              <Show when={props.config}>
                <Button variant="text" color="danger" size="sm" icon="material-symbols-light:delete-outline" iconSize={18} onClick={remove} aria-label="Quitar proveedor" title="Quitar proveedor" />
              </Show>
              <Button size="sm" onClick={save} disabled={saving()}>
                {saving() ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </div>
        </Card.Footer>
      </Show>
      <alert.AlertComponent />
    </Card>
  )
}

export default AIProviderCard
